/* eslint-disable */
import React, { useCallback } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { containsText } from "../../utils/utils";
import useThemeColors from "../../hooks/useThemeColors";
import { Slide, Slide as ToastSlide, toast } from "react-toastify";
import styles from "../../assets/jss/material-dashboard-pro-react/components/filterDialogStyle.js";


// @material-ui/core components
import {
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ListSubheader,
  InputAdornment,
} from "@material-ui/core";

import SearchIcon from "@material-ui/icons/Search";

// Custom Components
import Button from "components/CustomButtons/Button.js";
import ModalHeader from "components/ModalHeader";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";

// Third Party
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const FilterDialog = (props) => {
  const { clientColor } = props;
  const { clientTextColor } = useThemeColors();
  const classes = makeStyles(theme => styles(theme, clientColor))();
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useTranslation();

  const {
    open,
    onClose,
    headerTitle,
    headerScope,
    selectedItems,
    onItemsSelect,
    onSelectNone,
    onClearFilter,
    onApply,
    groupChoices,
    groupSelected,
    onGroupSelect,
    filterMode,
    allItems,
    refRangeSection,
  } = props;

  const ITEMS_LIMIT = 100;

    const notify = (text, type = "info", time = 3000) =>
      toast(t(text), {
        position: "top-right",
        autoClose: time,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
        theme: "light",
        type: type,
        transition: ToastSlide,
      });

  const filteredItems = useMemo(
    () => {
      if (!allItems) return [];
      
      // First, filter all items based on search query
      const allMatchingItems = allItems.filter(item => 
        item?.display && containsText(item.display.trim(), searchQuery)
      );
      
      // Then limit the display to ITEMS_LIMIT for performance
      return allMatchingItems.slice(0, ITEMS_LIMIT);
    },
    [allItems, searchQuery]
  );

  const renderSelectedValues = (selectedIndexes) => 
    (selectedIndexes?.length || 0) === 0 
      ? "" 
      : selectedIndexes
          .map(index => allItems.find(item => item.originalIndex === index)?.display)
          .filter(Boolean)
          .join(", ");

  const renderGroupMenuItems = () => {
    return groupChoices.map((item, key) => (
      <MenuItem
        classes={{
          root: classes.selectMenuItem,
          selected: classes.selectMenuItemSelected,
        }}
        value={item[0]}
        key={key}
      >
        {item[1]}
      </MenuItem>
    ));
  };

  const handleSelectAll = useCallback(() => {
    const indexes = Array.from({length: Math.min(allItems.length, ITEMS_LIMIT)}, (_, i) => i);
    onItemsSelect(indexes);
  }, [allItems, onItemsSelect]);

  const handleInvertSelection = useCallback(() => {
    const limitedItems = allItems.slice(0, ITEMS_LIMIT);
    const newSelection = limitedItems
      .map((_, index) => index)
      .filter(index => !selectedItems.includes(index));
    
    onItemsSelect(newSelection);
  }, [allItems, selectedItems, onItemsSelect]);

  const handleSelectDisplayed = useCallback((type) => (e) => {
    const displayedIndexes = filteredItems.map(item => item.originalIndex);
    let newSelection;
    
    if (type === 'all') {
      newSelection = [...new Set([...selectedItems, ...displayedIndexes])];
    } else {
      newSelection = selectedItems.filter(index => !displayedIndexes.includes(index));
    }
    
    onItemsSelect(newSelection);
  }, [filteredItems, selectedItems, onItemsSelect]);

  const handleApply = () => {
    // Ref range mode validation
    if (filterMode === "range") {
      if (!refRangeSection?.mode) {
        notify(t("results.toasts.you_must_select_at_least_one_item"));
        return;
      }
      if (refRangeSection.mode === "gt" && refRangeSection.min === "") return;
      if (refRangeSection.mode === "lt" && refRangeSection.max === "") return;
      if (refRangeSection.mode === "between" && (refRangeSection.min === "" || refRangeSection.max === "")) return;
      onApply();
      return;
    }
    if (
      (!selectedItems.length && !groupSelected) ||
      (filterMode === "items" && !selectedItems.length) ||
      (filterMode === "group" && !groupSelected)
    ) {
      notify(t("results.toasts.you_must_select_at_least_one_item"));
      return;
    }
    
    onApply();
  };

  return (
    <Dialog
      classes={{
        root: classes.modalRoot,
        paper: classes.modal,
      }}
      open={open}
      keepMounted
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <ModalHeader
        title={headerScope}
        subtitleParts={[<strong>{headerTitle}</strong>]}
        pills={
          selectedItems?.length
            ? [
                {
                  key: "selected",
                  label: t("results.filter_modal.n_selected", {
                    count: selectedItems.length,
                  }),
                },
              ]
            : []
        }
        onClose={onClose}
        closeLabel={t("common.close")}
        clientColor={clientColor}
      />

      <DialogContent className={classes.modalBody}>
        <GridContainer alignItems="center">
          <GridItem xs={12}>
            <GridContainer>
              <GridItem xs={12}>
                <FormControl
                  fullWidth
                  className={
                    classes.selectFormControl + " " + classes.marginBottomSmall
                  }
                >
                  <InputLabel
                    htmlFor="filter-items"
                    className={classes.selectLabel}
                  >
                    {t("results.filter_modal.select_items")}
                  </InputLabel>
                  <Select
                    MenuProps={{
                      autoFocus: false,
                      className: classes.selectMenu,
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                      PaperProps: {
                        style: {
                          maxHeight: 340,
                          overflow: "auto",
                        },
                      },
                    }}
                    classes={{
                      select: classes.select,
                    }}
                    value={selectedItems || []}
                    onChange={(e) => onItemsSelect(e.target.value)}
                    onClose={() => setSearchQuery("")}
                    renderValue={renderSelectedValues}
                    multiple
                  >
                    <ListSubheader style={{lineHeight: '34px'}}>
                      <TextField
                        size="small"
                        autoFocus
                        placeholder={t("results.filter_modal.search")}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                          classes: {
                            underline: classes.customUnderline,
                          },
                        }}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== "Escape") {
                            // Only stop propagation for typing events
                            e.stopPropagation();
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation(); // Stop propagation but don't prevent default
                        }}
                      />
                    </ListSubheader>
                    {searchQuery && (
                      <ListSubheader style={{padding: 0}}>
                        <div 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSelectDisplayed('all')(e);
                          }}
                          style={{
                            backgroundColor: '#fff',
                            padding: '10px 10px',
                            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{
                            color: '#999',
                            fontSize: '0.7rem',
                            fontWeight: 400,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            height: '10px'
                          }}>
                            {t("results.filter_modal.select_all_results")}
                          </span>
                        </div>
                        <div 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSelectDisplayed('none')(e);
                          }}
                          style={{
                            backgroundColor: '#fff',
                            padding: '10px 10px',
                            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{
                            color: '#999',
                            fontSize: '0.7rem',
                            fontWeight: 400,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            height: '10px'
                          }}>
                            {t("results.filter_modal.clear_all")}
                          </span>
                        </div>
                      </ListSubheader>
                    )}
                    {filteredItems.map((item) => (
                      <MenuItem
                        value={item.originalIndex}
                        key={item.originalIndex}
                        classes={{
                          root: classes.selectMenuItem,
                          selected: classes.selectMenuItemSelected,
                        }}
                        className={item.display.trim() === "" ? classes.unavailable : ""}
                      >
                        {item.display.trim() === "" ? t("results.blank") : item.display}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>

              <GridItem xs={12}>
                <GridContainer>
                  <GridItem className={classes.filterModalActionContainer}>
                    <div
                      className={classes.filterModalAction}
                      onClick={handleSelectAll}
                    >
                      {t("results.filter_modal.select_all")}
                    </div>
                  </GridItem>
                  <GridItem className={classes.filterModalActionContainer}>
                    <div
                      className={classes.filterModalAction}
                      onClick={onSelectNone}
                    >
                      {t("results.filter_modal.select_none")}
                    </div>
                  </GridItem>
                  <GridItem className={classes.filterModalActionContainer}>
                    <div
                      className={classes.filterModalAction}
                      onClick={handleInvertSelection}
                    >
                      {t("results.filter_modal.invert_selection")}
                    </div>
                  </GridItem>
                  <GridItem xs={true}>
                    <div className={classes.filterModalCounter}>
                      {selectedItems.length +
                        " " +
                        t("results.filter_modal.items_selected")}
                    </div>
                  </GridItem>
                </GridContainer>
              </GridItem>
            </GridContainer>
          </GridItem>

          {/* Group selector (for period, client groups, etc.) */}
          {groupChoices.length > 0 && [
              <GridItem
                key="up-arrow"
                xs={12}
                className={cx({
                  [classes.arrow]: true,
                  [classes.firstArrow]: true,
                  [classes.arrowSelected]: filterMode === "items",
                })}
              >
                <FontAwesomeIcon icon="chevron-up" />
              </GridItem>,

              <GridItem key="divider" xs={12}>
                <div className={classes.orDivider}>
                  {t("results.filter_modal.or")}
                </div>
              </GridItem>,

              <GridItem
                key="down-arrow"
                xs={12}
                className={cx({
                  [classes.arrow]: true,
                  [classes.arrowSelected]: filterMode === "group",
                })}
              >
                <FontAwesomeIcon icon="chevron-down" />
              </GridItem>,

              <GridItem key="group-select" xs={12} style={{ marginTop: "-7px" }}>
                <FormControl fullWidth className={classes.selectFormControl}>
                  <InputLabel
                    htmlFor="filter-group"
                    className={classes.selectLabel}
                  >
                    {t("results.filter_modal.select_a_range")}
                  </InputLabel>
                  <Select
                    MenuProps={{
                      className: classes.selectMenu,
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                    classes={{
                      select: classes.select,
                    }}
                    value={groupSelected}
                    onChange={onGroupSelect}
                    inputProps={{
                      name: "filterGroup",
                      id: "filter-group",
                    }}
                  >
                    {renderGroupMenuItems()}
                  </Select>
                </FormControl>
              </GridItem>
          ]}

          {/* Ref range section (filter by P&L reference — always last) */}
          {refRangeSection && refRangeSection.visible && [
              <GridItem key="divider-ref" xs={12}>
                <div className={classes.orDivider}>
                  {t("results.filter_modal.or")}
                </div>
              </GridItem>,

              <GridItem
                key="arrow-ref"
                xs={12}
                className={cx({
                  [classes.arrow]: true,
                  [classes.arrowSelected]: filterMode === "range",
                })}
              >
                <FontAwesomeIcon icon="chevron-down" />
              </GridItem>,

              <GridItem key="ref-range" xs={12} style={{ marginTop: "-7px" }}>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>
                    {t("results.filter_modal.filter_by_reference")}{" "}
                    <strong>{refRangeSection.refLineName}</strong>:
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                  {["gt", "lt", "between"].map((m) => (
                    <div
                      key={m}
                      onClick={() => refRangeSection.onModeChange(m)}
                      style={{
                        padding: "5px 12px",
                        borderRadius: 4,
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: refRangeSection.mode === m ? 600 : 400,
                        color: refRangeSection.mode === m ? clientTextColor : "#555",
                        backgroundColor: refRangeSection.mode === m ? (clientColor || '#999') : "#f5f5f5",
                        transition: "all 0.12s",
                      }}
                    >
                      {t("treemap.ref_filter_" + m)}
                    </div>
                  ))}
                </div>
                {refRangeSection.mode && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                    {(refRangeSection.mode === "gt" || refRangeSection.mode === "between") && (
                      <input
                        type="number"
                        value={refRangeSection.min}
                        onChange={(e) => refRangeSection.onMinChange(e.target.value)}
                        placeholder={refRangeSection.mode === "between" ? "Min %" : "%"}
                        style={{
                          width: refRangeSection.mode === "between" ? 90 : 100,
                          padding: "6px 8px",
                          borderRadius: 4,
                          border: "1px solid #ccc",
                          fontSize: 12,
                          outline: "none",
                        }}
                      />
                    )}
                    {refRangeSection.mode === "between" && (
                      <span style={{ fontSize: 11, color: "#999" }}>–</span>
                    )}
                    {(refRangeSection.mode === "lt" || refRangeSection.mode === "between") && (
                      <input
                        type="number"
                        value={refRangeSection.max}
                        onChange={(e) => refRangeSection.onMaxChange(e.target.value)}
                        placeholder={refRangeSection.mode === "between" ? "Max %" : "%"}
                        style={{
                          width: refRangeSection.mode === "between" ? 90 : 100,
                          padding: "6px 8px",
                          borderRadius: 4,
                          border: "1px solid #ccc",
                          fontSize: 12,
                          outline: "none",
                        }}
                      />
                    )}
                    <span style={{ fontSize: 11, color: "#999" }}>%</span>
                  </div>
                )}
              </GridItem>
          ]}
        </GridContainer>
      </DialogContent>

      <DialogActions className={classes.modalFooter}>
        <GridContainer>
          {onClearFilter && (
            <GridItem>
              <Button onClick={onClearFilter} color="info" simple>
                {t("results.filter_modal.clear_filter")}
              </Button>
            </GridItem>
          )}
          <GridItem xs={true}></GridItem>
          <GridItem>
            <Button onClick={handleApply} color="info" simple>
              {t("results.filter_modal.apply")}
            </Button>
            <Button onClick={onClose} color="danger" simple>
              {t("results.filter_modal.close")}
            </Button>
          </GridItem>
        </GridContainer>
      </DialogActions>
    </Dialog>
  );
};

FilterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  headerTitle: PropTypes.string.isRequired,
  headerScope: PropTypes.string.isRequired,
  selectedItems: PropTypes.array.isRequired,
  onItemsSelect: PropTypes.func.isRequired,
  onSelectNone: PropTypes.func.isRequired,
  onClearFilter: PropTypes.func,
  onApply: PropTypes.func.isRequired,
  groupChoices: PropTypes.array,
  groupSelected: PropTypes.string,
  onGroupSelect: PropTypes.func,
  filterMode: PropTypes.string.isRequired,
  clientColor: PropTypes.string,
  refRangeSection: PropTypes.shape({
    visible: PropTypes.bool,
    refLineName: PropTypes.string,
    mode: PropTypes.string,
    min: PropTypes.string,
    max: PropTypes.string,
    onModeChange: PropTypes.func,
    onMinChange: PropTypes.func,
    onMaxChange: PropTypes.func,
  }),
};

export default FilterDialog;
